import React from "react";
import { Typography } from "@mui/material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import SportsSoccerOutlinedIcon from "@mui/icons-material/SportsSoccerOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import LocationCityOutlinedIcon from "@mui/icons-material/LocationCityOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import "./worldCupFacts.scss";

interface Stat {
  icon: React.ReactNode;
  value: string;
  label: string;
  sub: string;
}

const STATS: Stat[] = [
  { icon: <GroupsOutlinedIcon />, value: "48", label: "Teams", sub: "up from 32 — the biggest ever" },
  { icon: <SportsSoccerOutlinedIcon />, value: "104", label: "Matches", sub: "up from 64 in 2022" },
  { icon: <PublicOutlinedIcon />, value: "3", label: "Host nations", sub: "a World Cup first" },
  { icon: <LocationCityOutlinedIcon />, value: "16", label: "Host cities", sub: "11 USA · 3 Mexico · 2 Canada" },
  { icon: <EventOutlinedIcon />, value: "39", label: "Days of football", sub: "Jun 11 – Jul 19, 2026" },
  { icon: <EmojiEventsOutlinedIcon />, value: "$50M", label: "Winner's prize", sub: "a record payout" },
];

interface Spotlight {
  tag: string;
  tone: "opening" | "final";
  title: string;
  venue: string;
  meta: string;
}

const SPOTLIGHTS: Spotlight[] = [
  {
    tag: "Opening Match",
    tone: "opening",
    title: "Mexico vs South Africa",
    venue: "Estadio Azteca · Mexico City",
    meta: "Thursday, June 11, 2026",
  },
  {
    tag: "The Final",
    tone: "final",
    title: "Who lifts the trophy?",
    venue: "MetLife Stadium · New York / New Jersey",
    meta: "Sunday, July 19, 2026",
  },
];

const FACTS: string[] = [
  "First World Cup ever co-hosted by three nations — Canada, Mexico and the United States.",
  "Estadio Azteca becomes the first stadium to host matches at three different men's World Cups (1970, 1986, 2026).",
  "Brand-new format: 12 groups of four, with the top two plus the eight best third-placed teams reaching a Round of 32.",
  "Four nations make their World Cup debut: Cape Verde, Curaçao, Jordan and Uzbekistan.",
  "For the first time, all six confederations have at least one guaranteed berth.",
  "Argentina arrive as defending champions, while four-time winners Italy failed to qualify.",
  "Eight stadiums will swap their artificial turf for natural grass just for the tournament.",
];

interface Mascot {
  emoji: string;
  name: string;
  animal: string;
  country: string;
}

const MASCOTS: Mascot[] = [
  { emoji: "🦌", name: "Maple", animal: "the Moose", country: "Canada" },
  { emoji: "🐆", name: "Zayu", animal: "the Jaguar", country: "Mexico" },
  { emoji: "🦅", name: "Clutch", animal: "the Bald Eagle", country: "USA" },
];

const WorldCupFacts: React.FC = () => (
  <div className="wcFacts">
    {/* Key numbers */}
    <section className="wcFacts__section">
      <Typography variant="h5" fontWeight={700} className="wcFacts__sectionTitle">
        The tournament by the numbers
      </Typography>
      <div className="wcFacts__statGrid">
        {STATS.map((stat) => (
          <div className="wcFacts__statCard" key={stat.label}>
            <span className="wcFacts__statIcon">{stat.icon}</span>
            <span className="wcFacts__statValue">{stat.value}</span>
            <span className="wcFacts__statLabel">{stat.label}</span>
            <span className="wcFacts__statSub">{stat.sub}</span>
          </div>
        ))}
      </div>
    </section>

    {/* Opening / Final spotlights */}
    <section className="wcFacts__section">
      <div className="wcFacts__spotlightRow">
        {SPOTLIGHTS.map((spot) => (
          <div
            className={`wcFacts__spotlight wcFacts__spotlight--${spot.tone}`}
            key={spot.tag}
          >
            <span className="wcFacts__spotlightTag">{spot.tag}</span>
            <Typography className="wcFacts__spotlightTitle">{spot.title}</Typography>
            <span className="wcFacts__spotlightVenue">{spot.venue}</span>
            <span className="wcFacts__spotlightMeta">{spot.meta}</span>
          </div>
        ))}
      </div>
    </section>

    {/* Did you know */}
    <section className="wcFacts__section">
      <Typography variant="h5" fontWeight={700} className="wcFacts__sectionTitle">
        Did you know?
      </Typography>
      <ul className="wcFacts__factsList">
        {FACTS.map((fact) => (
          <li className="wcFacts__factItem" key={fact}>
            <SportsSoccerIcon className="wcFacts__factIcon" />
            <span>{fact}</span>
          </li>
        ))}
      </ul>
    </section>

    {/* Mascots & ball */}
    <section className="wcFacts__section">
      <Typography variant="h5" fontWeight={700} className="wcFacts__sectionTitle">
        Meet the mascots &amp; the ball
      </Typography>
      <div className="wcFacts__mascotRow">
        {MASCOTS.map((mascot) => (
          <div className="wcFacts__mascotCard" key={mascot.name}>
            <span className="wcFacts__mascotEmoji" aria-hidden>
              {mascot.emoji}
            </span>
            <span className="wcFacts__mascotName">
              {mascot.name} <em>{mascot.animal}</em>
            </span>
            <span className="wcFacts__mascotCountry">{mascot.country}</span>
          </div>
        ))}
        <div className="wcFacts__mascotCard wcFacts__mascotCard--ball">
          <span className="wcFacts__mascotEmoji" aria-hidden>
            ⚽
          </span>
          <span className="wcFacts__mascotName">adidas Trionda</span>
          <span className="wcFacts__mascotCountry">
            “tri” (three hosts) + “onda” (wave) = triple wave
          </span>
        </div>
      </div>
    </section>
  </div>
);

export default WorldCupFacts;
